/**
 * Day separators for a chat thread — "Today", "Yesterday", then a real date,
 * the way every messaging client marks the passage of time in a long thread.
 */
import { formatInTimezone, timezoneDayKey } from '../../utils/timezone.js'

export interface DatedMessage {
  sent_at: string
}

/**
 * Midnight key in the *selected* timezone, so a message at 23:59 doesn't land
 * on the next day — and so the separators agree with the times printed on the
 * bubbles once the viewer's zone differs from their device's.
 */
function dayKey(iso: string | Date): string {
  return timezoneDayKey(iso)
}

export function dayLabel(iso: string): string {
  const date = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today.getTime() - 86_400_000)

  if (dayKey(iso) === dayKey(today)) return 'Today'
  if (dayKey(iso) === dayKey(yesterday)) return 'Yesterday'

  // Inside the last week the weekday is the most readable anchor.
  const daysApart = Math.floor((today.getTime() - date.getTime()) / 86_400_000)
  if (daysApart < 7 && daysApart > 0) {
    return formatInTimezone(date, { weekday: 'long' })
  }

  const sameYear = dayKey(date).slice(0, 4) === dayKey(today).slice(0, 4)
  return formatInTimezone(
    date,
    sameYear
      ? { day: 'numeric', month: 'long' }
      : { day: 'numeric', month: 'long', year: 'numeric' },
  )
}

/**
 * Marks the messages that open a new day, keyed by message id.
 *
 * Must be given the messages in **render order** (flattened across conversation
 * groups), not per group: bucketing each group independently repeats "Today"
 * at the head of every group, which is what a thread with several conversations
 * ends up looking like. Computing boundaries once over the whole rendered
 * sequence yields exactly one separator per calendar day.
 */
export function dayBoundaries<T extends DatedMessage & { id: string }>(
  messages: T[],
): Map<string, string> {
  const out = new Map<string, string>()
  let lastKey: string | null = null
  for (const msg of messages) {
    const key = dayKey(msg.sent_at)
    if (key !== lastKey) {
      out.set(msg.id, dayLabel(msg.sent_at))
      lastKey = key
    }
  }
  return out
}
